import { useLocalSearchParams } from "expo-router";
import Head from "expo-router/head";
import { useAtom, useAtomValue, useSetAtom } from "jotai";
import { DateTime } from "luxon";
import { useCallback, useEffect, useState } from "react";
import { ActivityIndicator, StyleSheet, View } from "react-native";

import type { EventFormValues } from "@/components/event-display";
import type { DisplayEvent } from "@/lib/event-helpers";

import { eventFamily } from "@/atoms/event";
import {
  eventFormFamily,
  eventRawFamily,
  eventTicketKindIdFamily,
} from "@/atoms/event-form";
import { currentUserIdAtom } from "@/atoms/session";
import { BackgroundImageBlur } from "@/components/BackgroundImageBlur";
import { EventDisplay } from "@/components/event-display";
import { GText } from "@/components/g-text";
import { colors } from "@/constants/colors";
import { useEventAutoSave } from "@/hooks/use-event-auto-save";
import { useEventMembers } from "@/hooks/use-event-members-sync";
import { usePayment } from "@/hooks/use-payment";
import Bugsnag from "@/lib/bugsnag";
import { toDisplayEvent } from "@/lib/event-helpers";
import { deleteTicketKind, fetchEventRawBySlug } from "@/lib/event-queries";

export default function EventScreen() {
  const { mode, slug } = useLocalSearchParams<{
    mode?: string;
    slug: string;
  }>();
  const isEditing = mode === "edit";
  const userId = useAtomValue(currentUserIdAtom);

  const [event, setEvent] = useAtom(eventFamily(slug));
  const setFormValues = useSetAtom(eventFormFamily(slug));
  const setRawState = useSetAtom(eventRawFamily(slug));
  const [ticketKindId, setTicketKindId] = useAtom(
    eventTicketKindIdFamily(slug),
  );
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<null | string>(null);
  const [flyerUrl, setFlyerUrl] = useState<null | string>(null);

  const eventId = event?.id ?? null;
  const { guests, hosts, myRsvp, refreshMember } = useEventMembers(
    eventId ?? "",
    userId,
  );

  const autoSave = useEventAutoSave(slug, eventId);

  useEffect(() => {
    if (!slug) return;
    setLoading(true);
    fetchEventRawBySlug(slug)
      .then((data) => {
        if (!data) {
          setError("event not found");
          return;
        }
        const raw = data as Record<string, unknown>;
        setRawState(raw);
        setEvent(toDisplayEvent(raw as Parameters<typeof toDisplayEvent>[0]));
        setFormValues(rawToInitialValues(raw));
        const tier = (
          raw.event_ticket_kind as Record<string, unknown>[] | undefined
        )?.[0];
        setTicketKindId((tier?.id as string) ?? null);
        if (raw.flyer_url) setFlyerUrl(raw.flyer_url as string);
      })
      .catch((e: unknown) => {
        Bugsnag.notify(e instanceof Error ? e : new Error(String(e)));
        setError("Failed to load event");
      })
      .finally(() => setLoading(false));
  }, [slug, setEvent, setFormValues, setRawState, setTicketKindId]);

  const handleFlyerUrlChange = useCallback((url: string) => {
    setFlyerUrl(url);
  }, []);

  const handleRemoveTicket = useCallback(async () => {
    if (!ticketKindId) return;
    await deleteTicketKind(ticketKindId);
    setTicketKindId(null);
    autoSave.patch({
      price_cents: "",
      quantity_total: "",
      ticket_description: "",
      ticket_name: "",
    });
  }, [ticketKindId, setTicketKindId, autoSave]);

  // Payment hook
  const paymentEvent = event ?? ({} as DisplayEvent);
  const {
    loading: paymentLoading,
    paymentUI,
    showSuccess,
    startPayment,
  } = usePayment(paymentEvent, myRsvp?.rsvp_status ?? null);

  if (loading) {
    return (
      <View style={styles.container}>
        <BackgroundImageBlur displayFlyerUrl={flyerUrl} />
        <View style={[styles.fill, styles.center]}>
          <ActivityIndicator color={colors.white} size="large" />
        </View>
      </View>
    );
  }

  if (error || !event) {
    return (
      <View style={styles.container}>
        <BackgroundImageBlur displayFlyerUrl={flyerUrl} />
        <View style={[styles.fill, styles.center]}>
          <GText style={styles.errorText}>whoops!</GText>
          <GText style={styles.errorText}>{error ?? "event not found"}</GText>
        </View>
      </View>
    );
  }

  const truncatedDescription = event.description
    ? event.description.slice(0, 160)
    : undefined;

  return (
    <View style={styles.container}>
      <BackgroundImageBlur displayFlyerUrl={flyerUrl} />

      {!isEditing && (
        <Head>
          {event.title ? <title>{event.title}</title> : null}
          {event.title ? (
            <meta content={event.title} property="og:title" />
          ) : null}
          {event.title ? (
            <meta content={event.title} name="twitter:title" />
          ) : null}
          {truncatedDescription ? (
            <meta content={truncatedDescription} name="description" />
          ) : null}
          {truncatedDescription ? (
            <meta content={truncatedDescription} property="og:description" />
          ) : null}
          {event.flyer_url ? (
            <meta content={event.flyer_url} property="og:image" />
          ) : null}
          {truncatedDescription ? (
            <meta content={truncatedDescription} name="twitter:description" />
          ) : null}
          {event.flyer_url ? (
            <meta content={event.flyer_url} name="twitter:image" />
          ) : null}
          <meta content="summary_large_image" name="twitter:card" />
        </Head>
      )}

      <EventDisplay
        autoSave={autoSave}
        event={event}
        guests={guests}
        hosts={hosts}
        mode={isEditing ? "edit" : "view"}
        myRsvp={myRsvp}
        onFlyerUrlChange={handleFlyerUrlChange}
        onGetTicket={startPayment}
        onRefreshMember={refreshMember}
        onRemoveTicket={handleRemoveTicket}
        onShareEvent={showSuccess}
        paymentUI={paymentUI}
        ticketLoading={paymentLoading}
      />
    </View>
  );
}

function rawToInitialValues(raw: Record<string, unknown>): EventFormValues {
  const tier = (
    raw.event_ticket_kind as Record<string, unknown>[] | undefined
  )?.[0];
  return {
    currency: (tier?.currency as string) ?? "usd",
    description: (raw.description as string) ?? "",
    duration_minutes: (() => {
      if (raw.starts_at && raw.ends_at) {
        const start = DateTime.fromISO(raw.starts_at as string);
        const end = DateTime.fromISO(raw.ends_at as string);
        if (start.isValid && end.isValid) {
          return String(Math.round(end.diff(start, "minutes").minutes));
        }
      }
      return "";
    })(),
    flyer_url: (raw.flyer_url as string) ?? "",
    location_city: (raw.location_city as string) ?? "",
    location_country: (raw.location_country as string) ?? "US",
    location_formatted_address: "",
    location_lat: "",
    location_lng: "",
    location_name: (raw.location_name as string) ?? "",
    location_neighborhood: (raw.location_neighborhood as string) ?? "",
    location_place_id: (raw.location_place_id as string) ?? "",
    location_postal_code: (raw.location_postal_code as string) ?? "",
    location_state: (raw.location_state as string) ?? "",
    location_street: (raw.location_street as string) ?? "",
    price_cents: tier ? String(tier.price_cents ?? 0) : "",
    price_service_fee: tier ? String(tier.price_service_fee ?? 0) : "",
    quantity_total: tier ? String(tier.quantity_total ?? 10) : "",
    starts_at: (raw.starts_at as string) ?? "",
    status: (raw.status as string) ?? "draft",
    ticket_description: (tier?.description as string) ?? "",
    ticket_name: (tier?.name as string) ?? "",
    timezone: (raw.timezone as string) ?? "America/Los_Angeles",
    title: (raw.title as string) ?? "",
    visibility: (raw.visibility as string) ?? "public",
  };
}

const styles = StyleSheet.create({
  center: {
    alignItems: "center",
    justifyContent: "center",
  },
  container: {
    backgroundColor: colors.transparent,
    flex: 1,
  },
  errorText: {
    color: colors.light,
    fontSize: 18,
  },
  fill: {
    flex: 1,
  },
});
